import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupportData } from '../../api';
import { SupportDashboardResponse, SupportInstance } from '../../types/SupportTypes';
import { useSupportInstanceSelection } from '../../context/SupportInstanceSelectionContext';
import SupportInstanceSelector from '../../components/SupportInstanceSelector';
import MetricCard from '../../components/MetricCard';
import ProductLegend from '../../components/ProductLegend';
import '../../styles/dashboard.css';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';
import MobileMetricSlider from '../../components/MobileMetricSlider';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Line, Pie, Bar } from 'react-chartjs-2';
import StaticAiPanel from '../../components/StaticAiPanel';
import {format} from 'date-fns';
import LoadingSpinner from '../../components/LoadingSpinner';


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);



// Hook for support data with instance filtering
export const useSupportData = () => {
  const [data, setData] = useState<SupportDashboardResponse | null>(null);
  const [instances, setInstances] = useState<SupportInstance[]>([]);
  const [filteredData, setFilteredData] = useState<SupportInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { selectedSupportCustomerIntegrationId, setSelectedSupportCustomerIntegrationId } = useSupportInstanceSelection();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        const supportData = await getSupportData();
        setData(supportData);
        
        if (supportData?.instances && supportData.instances.length > 0) {
          setInstances(supportData.instances);
          
          if (!selectedSupportCustomerIntegrationId && supportData.instances.length > 0) {
            setSelectedSupportCustomerIntegrationId(supportData.instances[0].customerIntegrationId);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch support data');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!data || !instances.length) {
      setFilteredData(null);
      return;
    }

    if (!selectedSupportCustomerIntegrationId) {
      const firstInstance = instances[0];
      setSelectedSupportCustomerIntegrationId(firstInstance.customerIntegrationId);
      setFilteredData(firstInstance);
      return;
    }

    const currentSelectedInstance = instances.find(i => i.customerIntegrationId === selectedSupportCustomerIntegrationId);

    if (!currentSelectedInstance) {
      const firstInstance = instances[0];
      setSelectedSupportCustomerIntegrationId(firstInstance.customerIntegrationId);
      setFilteredData(firstInstance);
      return;
    }

    setFilteredData(currentSelectedInstance);
  }, [data, selectedSupportCustomerIntegrationId, instances, setSelectedSupportCustomerIntegrationId]);

  return { data: filteredData, instances, loading, error };
};

const SupportOverview: React.FC = () => {
  const { data, instances, loading, error } = useSupportData();
  const { selectedSupportCustomerIntegrationId, setSelectedSupportCustomerIntegrationId } = useSupportInstanceSelection();
  const navigate = useNavigate();
  const [visibleProductsCases, setVisibleProductsCases] = useState<Record<string, boolean>>({});
  const [visibleProductsResolution, setVisibleProductsResolution] = useState<Record<string, boolean>>({});
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHighItemListExpanded, setIsHighItemListExpanded] = useState(false);
  const [showBacklogTooltip, setShowBacklogTooltip] = useState(false);
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet-portrait' | 'tablet-landscape' | 'desktop'>('desktop');
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize('mobile');
      } else if (width < 900) {
        setScreenSize('tablet-portrait');
      } else if (width < 1200) {
        setScreenSize('tablet-landscape');
      } else {
        setScreenSize('desktop');
      }
    };

    handleResize(); // Set initial size
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = screenSize === 'mobile';
  const isTabletPortrait = screenSize === 'tablet-portrait';
  const isTabletLandscape = screenSize === 'tablet-landscape';
  const shouldStack = isMobile || isTabletPortrait;

  // Prevent body scroll when drawer is open on mobile
  useEffect(() => {
    if (isMobile && drawerOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isMobile, drawerOpen]);

  // Modal handlers
  const openModal = (item: any) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  // Navigation handler for customer selection
  const navigateToCustomer = (customerName: string) => {
    navigate('/support/customers', {
      state: { selectedCustomer: customerName }
    });
  };

  // Navigation handler for team member selection
  const navigateToTeamMember = (memberName: string) => {
    navigate('/support/team-members', {
      state: { selectedMember: memberName }
    });
  };

  // Toggle product visibility for cases chart
  const toggleProductCases = (product: string) => {
    setVisibleProductsCases(prev => ({
      ...prev,
      [product]: !prev[product]
    }));
  };

  // Toggle product visibility for resolution time chart
  const toggleProductResolution = (product: string) => {
    setVisibleProductsResolution(prev => ({
      ...prev,
      [product]: !prev[product]
    }));
  };

  // Initialize visible products when data loads
  useEffect(() => {
    if (data?.productMonthlyTrendStats) {
      const products = Object.keys(data.productMonthlyTrendStats).sort();

      // For cases visibility: if more than 5 products, show only top 3 by total opened cases
      let casesVisibility: Record<string, boolean> = {};
      if (products.length > 5) {
        // Calculate sum of opened cases for each product
        const productCasesTotals = products.map(product => {
          const monthlyData = data.productMonthlyTrendStats[product];
          const totalOpenedCases = Object.values(monthlyData).reduce((sum: number, monthData: any) => sum + (monthData.assignedCases || 0), 0);
          return { product, totalOpenedCases };
        });

        // Sort by total resolved cases (descending) and take top 3
        const top3Products = productCasesTotals
          .sort((a, b) => b.totalOpenedCases - a.totalOpenedCases)
          .slice(0, 3)
          .map(item => item.product);

        // Set visibility - only top 3 visible
        products.forEach(product => {
          casesVisibility[product] = top3Products.includes(product);
        });
      } else {
        // Show all products if 5 or fewer
        products.forEach(product => {
          casesVisibility[product] = true;
        });
      }

      // For resolution time visibility: if more than 5 products, show only top 3 by sum of resolution time
      let resolutionVisibility: Record<string, boolean> = {};
      if (products.length > 5) {
        // Calculate sum of resolution time for each product
        const productResolutionTotals = products.map(product => {
          const monthlyData = data.productMonthlyTrendStats[product];
          const totalResolutionTime = Object.values(monthlyData).reduce((sum, monthData) => {
            return sum + (monthData.avgResolutionTime || 0);
          }, 0);
          return { product, totalResolutionTime };
        });

        // Sort by total resolution time (descending) and take top 3
        const top3Products = productResolutionTotals
          .sort((a, b) => b.totalResolutionTime - a.totalResolutionTime)
          .slice(0, 3)
          .map(item => item.product);

        // Set visibility - only top 3 visible
        products.forEach(product => {
          resolutionVisibility[product] = top3Products.includes(product);
        });
      } else {
        // Show all products if 5 or fewer
        products.forEach(product => {
          resolutionVisibility[product] = true;
        });
      }

      setVisibleProductsCases(casesVisibility);
      setVisibleProductsResolution(resolutionVisibility);
    }
  }, [data]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowBacklogTooltip(false);
    };

    if (showBacklogTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showBacklogTooltip]);

  if (loading) return <LoadingSpinner title="Loading support overview data..." />;
  if (error) return <div>Error: {error}</div>;
  if (!data || !data.summaryId) {
    return (
      <NoDataPlaceholder
        title="No Support Data Available"
        message={
          <>
            <p>To view support metrics and insights, you need to set up an integration with your support tools in the Settings page.</p>
            <p>After setting up the integration, it can take up to 10 minutes before the data is available in the dashboard.</p>
          </>
        }
      />
    );
  }
  const dataSnapshotRange = `${format(new Date(data.snapshotStartDate), 'do MMMM yyyy HH:mm')} 
  - ${format(new Date(data.summaryDate), 'do MMMM yyyy HH:mm')}`;

  // Helper function to format resolution time (in minutes)
  const formatResolutionTime = (minutes: number): string => {
    const isMinus = minutes < 0;
    minutes = Math.abs(minutes);

    let value = minutes.toString();

    if (minutes < 60) {
      value = `${minutes.toFixed(0)}m`;
    } else if (minutes < 1440) { // Less than 24 hours
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = Math.floor(minutes % 60);
      value = remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      const remainingMinutes = minutes % 1440;
      const hours = Math.floor(remainingMinutes / 60);
      const mins = Math.floor(remainingMinutes % 60);
      if (hours > 0 && mins > 0) {
        value = `${days}d ${hours}h ${mins}m`;
      } else if (hours > 0) {
        value = `${days}d ${hours}h`;
      } else if (mins > 0) {
        value = `${days}d ${mins}m`;
      } else {
        value = `${days}d`;
      }
    }
    if (isMinus) {
      value = `-${value}`;
    }
    return value;
  };

  // Helper function to format CSAT score. it is a number between 0 and 1 
  const formatCsatScore = (score?: number): string => {
    return score ? `${(score * 100).toFixed(1)}%` : 'N/A';
  };

  // Calculate high priority active cases (critical + high)
  const calculateHighPriorityOpenCases = () => {
    if (!data?.casesByPriority) return { count: 0, percent: 0 };

    let highPriorityOpenCount = 0;

    // Statuses to exclude (consider everything else as active)
    const inactiveStatuses = ['resolved', 'canceled', 'closed', 'removed'];

    // Check for critical priority active cases
    if (data.casesByPriority['critical']) {
      const criticalPriorityData = data.casesByPriority['critical'];
      Object.keys(criticalPriorityData).forEach(status => {
        if (!inactiveStatuses.includes(status.toLowerCase())) {
          highPriorityOpenCount += criticalPriorityData[status] || 0;
        }
      });
    }

    // Check for high priority active cases
    if (data.casesByPriority['high']) {
      const highPriorityData = data.casesByPriority['high'];
      Object.keys(highPriorityData).forEach(status => {
        if (!inactiveStatuses.includes(status.toLowerCase())) {
          highPriorityOpenCount += highPriorityData[status] || 0;
        }
      });
    }

    // Calculate percentage of all open cases
    const percent = data.casesOpen > 0 ? (highPriorityOpenCount / data.casesOpen) * 100 : 0;

    return {
      count: highPriorityOpenCount,
      percent
    };
  };

  const highPriorityOpenCases = calculateHighPriorityOpenCases();

  // Dynamic color generator function
  const generateColor = (index: number): string => {
    return colorPalette[index % colorPalette.length];
  };

  const colorPalette = [
    '#e6194b', // red
    '#3cb44b', // green
    '#ffe119', // yellow
    '#0082c8', // blue
    '#f58231', // orange
    '#911eb4', // purple
    '#46f0f0', // cyan
    '#f032e6', // magenta
    '#d2f53c', // lime
    '#fabebe', // pink
    '#008080', // teal
    '#e6beff', // lavender
    '#aa6e28', // brown
    '#fffac8', // beige
    '#800000', // maroon
    '#aaffc3', // mint
    '#808000', // olive
    '#ffd8b1', // apricot
    '#000080', // navy
    '#808080', // grey
    '#bcf60c', // chartreuse
    '#cafffb', // ice blue
    '#9a6324', // coffee
    '#fffac8', // light yellow
    '#800080', // deep purple
    '#a9a9a9', // dark gray
    '#b6fcd5', // pale green
    '#ffb6c1', // light pink
    '#4682b4', // steel blue
    '#ff7f50', // coral
  ];

  const canGenerateAiSummary = (aiGenratedDate: string | undefined): boolean => {
    return aiGenratedDate && data?.summaryDate ? new Date(aiGenratedDate) < new Date(data.summaryDate) : true;
  };

  const changeInstance = (instanceId: number) => {
    setSelectedSupportCustomerIntegrationId(instanceId);
  };

  // Process case priority data for stacked bar chart
  const processPriorityStatusData = () => {
    if (!data?.casesByPriority) return null;

    // Get all unique statuses across all priorities
    const allStatuses = new Set<string>();
    Object.values(data.casesByPriority).forEach(priorityData => {
      Object.keys(priorityData).forEach(status => allStatuses.add(status));
    });

    // Sort priorities by importance (critical > high > medium > low)
    const priorityOrder = ['critical', 'high', 'medium', 'low'];
    const priorities = Object.keys(data.casesByPriority).sort((a, b) => {
      const indexA = priorityOrder.indexOf(a.toLowerCase());
      const indexB = priorityOrder.indexOf(b.toLowerCase());
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    // Generate datasets - one dataset per status
    const datasets = Array.from(allStatuses).map((status, index) => {
      const color = generateColor(index);
      return {
        label: status,
        data: priorities.map(priority => data.casesByPriority[priority][status] || 0),
        backgroundColor: color,
      };
    });

    return {
      labels: priorities,
      datasets
    };
  };

  // Process backlog aging profile data for histogram
  const processBacklogAgingData = () => {
    if (!data?.backlogAgingProfile) return null;

    const agingData = data.backlogAgingProfile;

    // Define the expected order of age ranges
    const ageRangeOrder = ['0-7 days', '8-14 days', '15-30 days', '31-60 days', '>60 days'];

    // Sort labels according to the expected order
    const sortedLabels = Object.keys(agingData).sort((a, b) => {
      return ageRangeOrder.indexOf(a) - ageRangeOrder.indexOf(b);
    });

    const values = sortedLabels.map(label => agingData[label]);

    // Define color scale from green to red
    const colorScale: Record<string, string> = {
      '0-7 days': '#4caf50',     // Green
      '8-14 days': '#8bc34a',    // Light Green
      '15-30 days': '#ffeb3b',   // Yellow
      '31-60 days': '#ff9800',   // Orange
      '>60 days': '#f44336'      // Red
    };

    // Map colors to each age range
    const backgroundColors = sortedLabels.map(label => colorScale[label] || '#4bc0c0');
    const borderColors = backgroundColors.map(color =>
      color.replace('rgb', 'rgba').replace(')', ', 0.8)')
    );

    return {
      labels: sortedLabels,
      datasets: [
        {
          label: 'Number of Open Cases',
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.7,
        },
      ],
    };
  };

  // Process product monthly trend data for Chart.js
  const processProductTrendData = () => {


    const monthlyStats = data.productMonthlyTrendStats;

    // Get all unique products (top-level keys)
    const products = Object.keys(monthlyStats).sort();

    // Get all unique months from all products
    const allMonths = new Set<string>();
    products.forEach(product => {
      const productData = monthlyStats[product];
      if (productData) {
        Object.keys(productData).forEach(month => {
          allMonths.add(month);
        });
      }
    });

    const months = Array.from(allMonths).sort();

    // Format months for better display (assuming format is YYYY-MM or similar)
    const formattedMonths = months.map(month => {
      // If month is in YYYY-MM format, convert to more readable format
      if (month.includes('-')) {
        const [year, monthNum] = month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
      }
      return month;
    });

    // Sort products by total assigned cases across all months
    const productTotals = products.map(product => {
      const totalAssigned = months.reduce((sum, month) => {
        const productData = monthlyStats[product]?.[month];
        return sum + (productData?.assignedCases || 0);
      }, 0);
      return { product, totalAssigned };
    }).sort((a, b) => b.totalAssigned - a.totalAssigned);

    const sortedProductsByAssignedCases = productTotals.map(item => item.product);

    // Create datasets for cases opened
    const casesOpenedDatasets = sortedProductsByAssignedCases.map((product, index) => {
      const color = generateColor(index);
      return {
        label: product,
        data: months.map(month => {
          const productData = monthlyStats[product]?.[month];
          return productData?.assignedCases || 0;
        }),
        borderColor: color,
        backgroundColor: `${color}20`,
        tension: 0.4,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
      };
    });



    // Calculate average resolution time across all months for each product
    const productAvgResolutionTotals = products.map(product => {
      const avgResolutionTimeTotal = months.reduce((sum, month) => {
        const productData = monthlyStats[product]?.[month];
        return sum + (productData?.avgResolutionTime || 0);
      }, 0);
      return { product, avgResolutionTimeTotal };
    }).sort((a, b) => b.avgResolutionTimeTotal - a.avgResolutionTimeTotal);

    const sortedProductsByResolutionTime = productAvgResolutionTotals.map(item => item.product);

    // Create datasets for average resolution time
    const avgResolutionTimeDatasets = sortedProductsByResolutionTime.map((product, index) => {
      const color = generateColor(index);
      return {
        label: product,
        data: months.map(month => {
          const productData = monthlyStats[product]?.[month];
          return (productData?.avgResolutionTime || 0) / 60;
        }),
        borderColor: color,
        backgroundColor: `${color}20`,
        tension: 0.4,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
      };
    });

    return {
      months,
      formattedMonths,
      products,
      sortedProductsByAssignedCases,
      sortedProductsByResolutionTime,
      casesOpenedData: {
        labels: formattedMonths,
        datasets: casesOpenedDatasets,
      },
      avgResolutionTimeData: {
        labels: formattedMonths,
        datasets: avgResolutionTimeDatasets,
      }
    };
  };

  // Filter datasets based on visible products
  const filterDatasetsCases = (datasets: any[]) => {
    return datasets.filter(dataset => visibleProductsCases[dataset.label]);
  };

  const filterDatasetsResolution = (datasets: any[]) => {
    return datasets.filter(dataset => visibleProductsResolution[dataset.label]);
  };

  // Get filtered chart data
  const getFilteredChartDataCases = (chartData: any) => {
    if (!chartData) return chartData;
    return {
      ...chartData,
      datasets: filterDatasetsCases(chartData.datasets)
    };
  };

  const getFilteredChartDataResolution = (chartData: any) => {
    if (!chartData) return chartData;
    return {
      ...chartData,
      datasets: filterDatasetsResolution(chartData.datasets)
    };
  };

  const productTrendData = processProductTrendData();

  // Chart options
  const casesOpenedOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide default legend
      },
      title: {
        display: true,
        text: 'Cases Opened by Product Over Time',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Cases Opened',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Month',
        },
      },
    },
  };

  const avgResolutionTimeOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide default legend
      },
      title: {
        display: true,
        text: 'Average Resolution Time by Product',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Resolution Time (hours)',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Month',
        },
      },
    },
  };


  const metricCardColorGood = '#f4ffed'; //green
  const metricCardColorBad = '#fad6d4'; //red
  const metricCardColorNeutral = '#fce8ac'; //white

  const metricCardValueColorGood = '#43a047'; //green
  const metricCardValueColorBad = '#e53935'; //red
  const metricCardValueColorNeutral = '#877814'; //yellow;

  return (
    <div style={{ 
      padding: isMobile ? '1rem' : '1rem 2rem 2rem 2rem', 
      margin: '0 auto',
      paddingBottom: isMobile ? '120px' : '2rem', // Extra space for mobile drawer
      maxWidth: '90vw'
    }}>
      {/* Mobile fixed top instance selector */}
      {isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            paddingTop: 40,
            background: '#ffffff',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ maxWidth: 1400, margin: '0 auto', padding: '12px 16px' }}>
            <div style={{
              fontSize: 20,
              fontWeight: 300,
              color: '#374151',
              marginBottom: 8,
              textAlign: 'center',
              fontFamily: 'Raleway, sans-serif'
            }}>
              Support Overview
            </div>
            {instances && instances.length > 0 && (
              <select
                value={selectedSupportCustomerIntegrationId || ''}
                onChange={(e) => changeInstance(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#f9fafb',
                  fontSize: 14,
                  color: '#111827'
                }}
              >
                {instances.map((instance) => (
                  <option key={instance.customerIntegrationId} value={instance.customerIntegrationId}>
                    {instance.instanceName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}
      {/* Spacer to offset fixed header height on mobile */}
      {isMobile && <div style={{ height: 70 }} />}

      {/* Desktop header - hidden on mobile */}
      {!isMobile && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h1 className="dashboard-title" style={{
            fontSize: '2rem',
            lineHeight: '1.2',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 300,
            margin: 0
          }}>
            {data?.instanceName || 'Support Overview'}
          </h1>
          
          {instances.length > 0 && (
            <SupportInstanceSelector
              instances={instances}
              selectedCustomerIntegrationId={selectedSupportCustomerIntegrationId}
              onSelectInstance={changeInstance}
            />
          )}
        </div>
      )}
      {data?.summaryDate && (
        <p className="dashboard-subtitle" style={{ 
          marginBottom: '2rem', 
          color: '#666', 
          fontSize: isMobile ? '0.8rem' : '0.9rem' 
        }}>
          Data snapshot: {dataSnapshotRange}
        </p>
      )}

      {/* Key Metrics Grid - Desktop */}
      <div 
        style={{ 
          display: isMobile ? 'none' : 'grid', 
          gridTemplateColumns: 'repeat(6, 1fr)', 
          gap: '1rem', 
          marginBottom: '2rem' 
        }}
      >
        {/* High Priority Open Cases */}
        <MetricCard
          title="Critical/High Priority Open"
          frontCardColor={(() => {
            if (highPriorityOpenCases.count < 1) { //should be no open cases
              return metricCardColorGood;
            } else {
              return metricCardColorBad;
            }
          })()}
          description={`${highPriorityOpenCases.percent.toFixed(1)}% of ${data.casesOpen} open cases`}
          descriptionColor={(() => {
            if (highPriorityOpenCases.count < 1) { //should be no open cases
              return metricCardValueColorGood;
            } else {
              return metricCardValueColorBad;
            }
          })()}
          value={highPriorityOpenCases.count}
          frontValueColor={(() => {
            if (highPriorityOpenCases.count < 1) { //should be no open cases
              return metricCardValueColorGood;
            } else {
              return metricCardValueColorBad;
            }
          })()}
          isNegative={highPriorityOpenCases.count > 0}
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ transition: 'stroke 0.2s ease' }}
            >
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
              <line x1="12" y1="11" x2="12" y2="16" />
            </svg>
          }
          canFlip={true}
          backCardColor={(() => {
            if (!data.priorityMonthlyTrendStats) return metricCardColorNeutral;

            const months = Object.keys(data.priorityMonthlyTrendStats).sort();
            if (months.length < 2) return metricCardColorNeutral;

            const currentMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const currentCriticalOpened = data.priorityMonthlyTrendStats[currentMonth]?.critical?.opened || 0;
            const currentHighOpened = data.priorityMonthlyTrendStats[currentMonth]?.high?.opened || 0;
            const currentTotal = currentCriticalOpened + currentHighOpened;

            const previousCriticalOpened = data.priorityMonthlyTrendStats[previousMonth]?.critical?.opened || 0;
            const previousHighOpened = data.priorityMonthlyTrendStats[previousMonth]?.high?.opened || 0;
            const previousTotal = previousCriticalOpened + previousHighOpened;

            // For opened critical/high cases, lower is better
            return currentTotal <= previousTotal ? metricCardColorGood : metricCardColorBad;
          })()}
          backTitle='Critical/High Priority Opened This Month'
          backValue={(() => {
            if (!data.priorityMonthlyTrendStats) return 'No data';

            const months = Object.keys(data.priorityMonthlyTrendStats).sort();
            if (months.length === 0) return 'No data';

            const currentMonth = months[months.length - 1];
            const currentMonthData = data.priorityMonthlyTrendStats[currentMonth];

            if (!currentMonthData) return 'No data';

            // Sum opened cases for critical and high priorities
            const criticalOpened = currentMonthData.critical?.opened || 0;
            const highOpened = currentMonthData.high?.opened || 0;
            const totalOpened = criticalOpened + highOpened;

            return totalOpened.toString();
          })()}
          backValueColor={(() => {
            if (!data.priorityMonthlyTrendStats) return metricCardValueColorNeutral;

            const months = Object.keys(data.priorityMonthlyTrendStats).sort();
            if (months.length < 2) return metricCardValueColorNeutral;

            const currentMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const currentCriticalOpened = data.priorityMonthlyTrendStats[currentMonth]?.critical?.opened || 0;
            const currentHighOpened = data.priorityMonthlyTrendStats[currentMonth]?.high?.opened || 0;
            const currentTotal = currentCriticalOpened + currentHighOpened;

            const previousCriticalOpened = data.priorityMonthlyTrendStats[previousMonth]?.critical?.opened || 0;
            const previousHighOpened = data.priorityMonthlyTrendStats[previousMonth]?.high?.opened || 0;
            const previousTotal = previousCriticalOpened + previousHighOpened;

            // For opened critical/high cases, lower is better
            return currentTotal <= previousTotal ? metricCardValueColorGood : metricCardValueColorBad;
          })()}
          backDescription={(() => {
            if (!data.priorityMonthlyTrendStats) return 'No trend data available';

            const months = Object.keys(data.priorityMonthlyTrendStats).sort();
            if (months.length < 2) return 'Insufficient data for trend';

            const currentMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const currentCriticalOpened = data.priorityMonthlyTrendStats[currentMonth]?.critical?.opened || 0;
            const currentHighOpened = data.priorityMonthlyTrendStats[currentMonth]?.high?.opened || 0;
            const currentTotal = currentCriticalOpened + currentHighOpened;

            const previousCriticalOpened = data.priorityMonthlyTrendStats[previousMonth]?.critical?.opened || 0;
            const previousHighOpened = data.priorityMonthlyTrendStats[previousMonth]?.high?.opened || 0;
            const previousTotal = previousCriticalOpened + previousHighOpened;

            if (previousTotal === 0 && currentTotal === 0) return 'No change from previous month';
            if (previousTotal === 0) return `↑ ${currentTotal} more than previous month`;

            const difference = currentTotal - previousTotal;
            const percentChange = ((difference / previousTotal) * 100).toFixed(1);
            const trend = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';

            return `${trend} ${Math.abs(difference)} (${Math.abs(Number(percentChange))}%) vs previous month`;
          })()}
          backDescriptionColor={(() => {
            if (!data.priorityMonthlyTrendStats) return metricCardValueColorNeutral;

            const months = Object.keys(data.priorityMonthlyTrendStats).sort();
            if (months.length < 2) return metricCardValueColorNeutral;

            const currentMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const currentCriticalOpened = data.priorityMonthlyTrendStats[currentMonth]?.critical?.opened || 0;
            const currentHighOpened = data.priorityMonthlyTrendStats[currentMonth]?.high?.opened || 0;
            const currentTotal = currentCriticalOpened + currentHighOpened;

            const previousCriticalOpened = data.priorityMonthlyTrendStats[previousMonth]?.critical?.opened || 0;
            const previousHighOpened = data.priorityMonthlyTrendStats[previousMonth]?.high?.opened || 0;
            const previousTotal = previousCriticalOpened + previousHighOpened;

            // For opened critical/high cases, lower is better
            return currentTotal <= previousTotal ? metricCardValueColorGood : metricCardValueColorBad;
          })()}
        />

        {/* CSAT Average Score */}
        <MetricCard
          title="Customer Satisfaction (Snapshot)"
          value={formatCsatScore(data.csatData?.CsatAverageScore)}
          description={data.csatData?.CsatTotalResponses
            ? `${data.csatData.CsatTotalResponses} responses`
            : 'No responses yet'
          }
          frontValueColor={
            data.csatData
              ? ((data.csatData?.CsatAverageScore || 0) < 0.6
                ? ((data.csatData?.CsatAverageScore || 0) < 0.4 ? metricCardValueColorBad : metricCardValueColorNeutral)
                : metricCardValueColorGood)
              : metricCardValueColorNeutral
          }
          descriptionColor={
            data.csatData
              ? ((data.csatData?.CsatAverageScore || 0) < 0.6
                ? ((data.csatData?.CsatAverageScore || 0) < 0.4 ? metricCardValueColorBad : metricCardValueColorNeutral)
                : metricCardValueColorGood)
              : metricCardValueColorNeutral
          }
          frontCardColor={
            data.csatData
              ? ((data.csatData?.CsatAverageScore || 0) < 0.6
                ? ((data.csatData?.CsatAverageScore || 0) < 0.4 ? metricCardColorBad : metricCardColorNeutral)
                : metricCardColorGood)
              : metricCardColorNeutral
          }
          backValueColor={
            (() => {
              if (!data.csatMonthlyAverage) return metricCardValueColorNeutral;

              const months = Object.keys(data.csatMonthlyAverage).sort();
              if (months.length < 2) return metricCardValueColorNeutral;

              const latestMonth = months[months.length - 1];
              const previousMonth = months[months.length - 2];

              const latestScore = data.csatMonthlyAverage[latestMonth].averageNormalizedScore;
              const previousScore = data.csatMonthlyAverage[previousMonth].averageNormalizedScore;

              return latestScore >= previousScore ? metricCardValueColorGood : metricCardValueColorBad;
            })()
          }
          backCardColor={
            (() => {
              if (!data.csatMonthlyAverage) return metricCardColorNeutral;

              const months = Object.keys(data.csatMonthlyAverage).sort();
              if (months.length < 2) return metricCardColorNeutral;

              const latestMonth = months[months.length - 1];
              const previousMonth = months[months.length - 2];

              const latestScore = data.csatMonthlyAverage[latestMonth].averageNormalizedScore;
              const previousScore = data.csatMonthlyAverage[previousMonth].averageNormalizedScore;

              return latestScore >= previousScore ? metricCardColorGood : metricCardColorBad;
            })()
          }
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <circle cx="9" cy="9" r="0.5" fill="currentColor" />
              <circle cx="15" cy="9" r="0.5" fill="currentColor" />
            </svg>
          }
          canFlip={true}
          backTitle='Customer Satisfaction (Current Month)'
          backValue={(() => {
            if (!data.csatMonthlyAverage) return 'No data';

            // Get months in chronological order
            const months = Object.keys(data.csatMonthlyAverage).sort();
            if (months.length === 0) return 'No data';

            // Get the most recent month's score
            const latestMonth = months[months.length - 1];
            const latestScore = data.csatMonthlyAverage[latestMonth].averageNormalizedScore;

            return formatCsatScore(latestScore);
          })()}
          backDescription={(() => {
            if (!data.csatMonthlyAverage) return 'No trend data available';

            // Get months in chronological order
            const months = Object.keys(data.csatMonthlyAverage).sort();
            if (months.length < 2) return 'Not enough data for trend';

            // Get the most recent and previous month's scores
            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestNumberOfResponses = data.csatMonthlyAverage[latestMonth].totalResponses;
            const latestScore = data.csatMonthlyAverage[latestMonth].averageNormalizedScore;
            const previousScore = data.csatMonthlyAverage[previousMonth].averageNormalizedScore;



            // Calculate percentage change
            const percentChange = ((latestScore - previousScore) / previousScore) * 100;

            const trend = percentChange > 0 ? '↑' : percentChange < 0 ? '↓' : '→';

            // Format the description
            return `${trend} ${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(1)}% vs previous month. (${latestNumberOfResponses} responses)`;
          })()}
          backDescriptionColor={(() => {
            if (!data.csatMonthlyAverage) return metricCardValueColorNeutral;

            const months = Object.keys(data.csatMonthlyAverage).sort();
            if (months.length < 2) return metricCardValueColorNeutral;

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestScore = data.csatMonthlyAverage[latestMonth].averageNormalizedScore;
            const previousScore = data.csatMonthlyAverage[previousMonth].averageNormalizedScore;

            // Return green for positive trend, red for negative
            return latestScore >= previousScore ? metricCardValueColorGood : metricCardValueColorBad;
          })()}
        />

        {/* SLA Breaches */}
        <MetricCard
          title="SLA Breaches (Snapshot)"
          value={data.slaBreaches.count}
          description={`${data.slaBreaches.percent.toFixed(1)}% of ${data.casesTotal} total cases`}
          isNegative={true}
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ transition: 'stroke 0.2s ease' }}
            >
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
              <line x1="12" y1="11" x2="12" y2="16" />
            </svg>
          }
          canFlip={true}
          backTitle='SLA Breaches (Current Month)'
          frontValueColor={data.slaBreaches.count > 0 ? metricCardValueColorBad : metricCardValueColorGood}
          descriptionColor={data.slaBreaches.count > 0 ? metricCardValueColorBad : metricCardValueColorGood}
          backValue={
            data.monthlySlaBreaches ?
              (() => {
                // Get the last two months of data
                const months = Object.keys(data.monthlySlaBreaches)
                  .sort()
                  .slice(-2);

                if (months.length === 0) return '0';

                const latestMonth = months[months.length - 1];
                return `${data.monthlySlaBreaches[latestMonth].breachCount} (${data.monthlySlaBreaches[latestMonth].breachRate.toFixed(1)}%)`;
              })() : '0'
          }
          backDescription={
            data.monthlySlaBreaches ?
              (() => {
                // Get the last two months of data
                const months = Object.keys(data.monthlySlaBreaches)
                  .sort()
                  .slice(-2);

                if (months.length < 2) return '→ No breaches recorded';

                const currentMonth = months[1];
                const previousMonth = months[0];
                const currentRate = data.monthlySlaBreaches[currentMonth].breachRate;
                const previousRate = data.monthlySlaBreaches[previousMonth].breachRate;
                const difference = currentRate - previousRate;

                const trend = difference > 0 ? 'up' : difference < 0 ? 'down' : 'stable';
                const trendSymbol = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';

                return `${trendSymbol} ${Math.abs(difference).toFixed(1)}% vs previous month (${previousMonth})`;
              })() : '→ No breaches recorded'
          }
          backDescriptionColor={(() => {
            if (!data.monthlySlaBreaches) return undefined;

            const months = Object.keys(data.monthlySlaBreaches)
              .sort()
              .slice(-2);

            if (months.length < 2) return metricCardValueColorGood;

            const currentMonth = months[1];
            const previousMonth = months[0];
            const currentRate = data.monthlySlaBreaches[currentMonth].breachRate;
            const previousRate = data.monthlySlaBreaches[previousMonth].breachRate;
            const difference = currentRate - previousRate;

            return difference > 0 ? metricCardValueColorBad : difference < 0 ? metricCardValueColorGood : metricCardValueColorNeutral;
          })()}

          backValueColor={(() => {
            if (!data.monthlySlaBreaches) return metricCardValueColorGood;

            const months = Object.keys(data.monthlySlaBreaches)
              .sort()
              .slice(-2);

            if (months.length < 2) return metricCardValueColorGood;

            const currentMonth = months[1];
            const previousMonth = months[0];
            const currentRate = data.monthlySlaBreaches[currentMonth].breachRate;
            const previousRate = data.monthlySlaBreaches[previousMonth].breachRate;
            const difference = currentRate - previousRate;

            return difference > 0 ? metricCardValueColorBad : difference < 0 ? metricCardValueColorGood : metricCardValueColorNeutral;
          })()}
          frontCardColor={
            data.slaBreaches.count > 0 ? metricCardColorBad : metricCardColorGood
          }
          backCardColor={(() => {
            if (!data.monthlySlaBreaches) return metricCardColorGood;

            const months = Object.keys(data.monthlySlaBreaches)
              .sort()
              .slice(-2);

            if (months.length < 2) return metricCardColorGood;

            const currentMonth = months[1];
            const previousMonth = months[0];
            const currentRate = data.monthlySlaBreaches[currentMonth].breachRate;
            const previousRate = data.monthlySlaBreaches[previousMonth].breachRate;
            const difference = currentRate - previousRate;

            return difference > 0 ? metricCardColorBad : difference < 0 ? metricCardColorGood : metricCardColorNeutral;
          })()}
        />

        {/* Open Cases */}
        <MetricCard
          title="Still Open Cases (Snapshot)"
          frontCardColor={((data.casesOpen / data.casesTotal) * 100) > 20 ? metricCardColorBad : metricCardColorGood}
          value={data.casesOpen}
          frontValueColor={((data.casesOpen / data.casesTotal) * 100) > 20 ? metricCardValueColorBad : metricCardValueColorGood}
          description={`${((data.casesOpen / data.casesTotal) * 100).toFixed(1)}% of ${data.casesTotal} total raised cases`}
          descriptionColor={((data.casesOpen / data.casesTotal) * 100) > 20 ? metricCardValueColorBad : metricCardValueColorGood}
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10,9 9,9 8,9" />
            </svg>
          }
          canFlip={true}
          backCardColor={(() => {
            if (!data.monthlyCaseStats) return metricCardColorNeutral;

            const months = Object.keys(data.monthlyCaseStats.monthlyCasesByProduct).sort();
            if (months.length < 2) return metricCardColorNeutral;

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestData = data.monthlyCaseStats.monthlyCasesByProduct[latestMonth]?.DefaultAllProduct;
            const previousData = data.monthlyCaseStats.monthlyCasesByProduct[previousMonth]?.DefaultAllProduct;

            if (!latestData || !previousData) return metricCardColorNeutral;

            // Lower opened cases is better
            return latestData.opened <= previousData.opened ?
              metricCardColorGood : metricCardColorBad;
          })()}
          backTitle='Newly Opened Cases (Current Month)'
          backValue={(() => {
            if (!data.monthlyCaseStats) return '0';

            const months = Object.keys(data.monthlyCaseStats.monthlyCasesByProduct).sort();
            if (months.length === 0) return '0';

            const latestMonth = months[months.length - 1];
            const productData = data.monthlyCaseStats.monthlyCasesByProduct[latestMonth]?.DefaultAllProduct;

            return productData ? productData.opened.toString() : '0';
          })()}
          backValueColor={(() => {
            if (!data.monthlyCaseStats) return metricCardValueColorNeutral;

            const months = Object.keys(data.monthlyCaseStats.monthlyCasesByProduct).sort();
            if (months.length < 2) return metricCardValueColorNeutral;

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestData = data.monthlyCaseStats.monthlyCasesByProduct[latestMonth]?.DefaultAllProduct;
            const previousData = data.monthlyCaseStats.monthlyCasesByProduct[previousMonth]?.DefaultAllProduct;

            if (!latestData || !previousData) return metricCardValueColorNeutral;

            // Lower opened cases is better
            return latestData.opened <= previousData.opened ?
              metricCardValueColorGood : metricCardValueColorBad;
          })()}
          backDescription={(() => {
            if (!data.monthlyCaseStats) return 'No monthly data available';

            const months = Object.keys(data.monthlyCaseStats.monthlyCasesByProduct).sort();
            if (months.length < 2) return 'Insufficient data for trend';

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestData = data.monthlyCaseStats.monthlyCasesByProduct[latestMonth]?.DefaultAllProduct;
            const previousData = data.monthlyCaseStats.monthlyCasesByProduct[previousMonth]?.DefaultAllProduct;

            if (!latestData || !previousData) return 'Insufficient data for trend';

            const difference = latestData.opened - previousData.opened;
            const percentChange = ((difference / previousData.opened) * 100).toFixed(1);
            const trend = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';

            return `${trend} ${Math.abs(difference)} cases (${Math.abs(Number(percentChange))}%) vs previous month`;
          })()}
          backDescriptionColor={(() => {
            if (!data.monthlyCaseStats) return metricCardValueColorNeutral;

            const months = Object.keys(data.monthlyCaseStats.monthlyCasesByProduct).sort();
            if (months.length < 2) return metricCardValueColorNeutral;

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestData = data.monthlyCaseStats.monthlyCasesByProduct[latestMonth]?.DefaultAllProduct;
            const previousData = data.monthlyCaseStats.monthlyCasesByProduct[previousMonth]?.DefaultAllProduct;

            if (!latestData || !previousData) return metricCardValueColorNeutral;

            // Lower opened cases is better
            return latestData.opened <= previousData.opened ?
              metricCardValueColorGood : metricCardValueColorBad;
          })()}
        />

        {/*average first response time */}
        <MetricCard
          title="First Response Time (Snapshot)"
          description="Average Time to First Response"
          descriptionColor={(() => {
            if (!data.avgFirstResponseTime) return metricCardValueColorNeutral;
            if (data.avgFirstResponseTime < 120) { //2 hours
              return metricCardValueColorGood;
            } else {
              return metricCardValueColorBad;
            }
          })()}
          value={(() => {
            if (!data.avgFirstResponseTime) return 'No data';
            return formatResolutionTime(data.avgFirstResponseTime);
          })()}
          frontValueColor={(() => {
            if (!data.avgFirstResponseTime) return metricCardValueColorNeutral;
            if (data.avgFirstResponseTime < 120) { //2 hours
              return metricCardValueColorGood;
            } else {
              return metricCardValueColorBad;
            }
          })()}
          frontCardColor={(() => {
            if (!data.avgFirstResponseTime) return metricCardColorNeutral;
            if (data.avgFirstResponseTime < 120) { //2 hours
              return metricCardColorGood;
            } else {
              return metricCardColorBad;
            }
          })()}
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12,6 12,12 16,14" />
            <line x1="12" y1="20" x2="12" y2="22" />
            <line x1="20" y1="12" x2="22" y2="12" />
            <line x1="2" y1="12" x2="4" y2="12" />
            {/* Stopwatch pushers (nobbly buttons) */}
            <circle cx="16" cy="0" r="0.5" />
            <circle cx="8" cy="0" r="0.5" />

          </svg>
          }
          canFlip={true}
          backTitle='First Response Time (Current Month)'
          backCardColor={(() => {
            if (!data.monthlyAverageFirstResponseTime) return metricCardColorNeutral;

            const months = Object.keys(data.monthlyAverageFirstResponseTime).sort();
            if (months.length < 2) return metricCardColorNeutral;

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestAvg = data.monthlyAverageFirstResponseTime[latestMonth]?.average;
            const previousAvg = data.monthlyAverageFirstResponseTime[previousMonth]?.average;

            if (!latestAvg || !previousAvg) return metricCardColorNeutral;

            // Lower response time is better
            return latestAvg <= previousAvg ? metricCardColorGood : metricCardColorBad;
          })()}
          backValue={(() => {
            if (!data.monthlyAverageFirstResponseTime) return 'No data';

            const months = Object.keys(data.monthlyAverageFirstResponseTime).sort();
            if (months.length === 0) return 'No data';

            const latestMonth = months[months.length - 1];
            const latestData = data.monthlyAverageFirstResponseTime[latestMonth];

            if (!latestData) return 'No data';

            return formatResolutionTime(latestData.average);
          })()}
          backValueColor={(() => {
            if (!data.monthlyAverageFirstResponseTime) return metricCardValueColorNeutral;

            const months = Object.keys(data.monthlyAverageFirstResponseTime).sort();
            if (months.length < 2) return metricCardValueColorNeutral;

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestAvg = data.monthlyAverageFirstResponseTime[latestMonth]?.average;
            const previousAvg = data.monthlyAverageFirstResponseTime[previousMonth]?.average;

            if (!latestAvg || !previousAvg) return metricCardValueColorNeutral;

            // Lower response time is better
            return latestAvg <= previousAvg ? metricCardValueColorGood : metricCardValueColorBad;
          })()}
          backDescription={(() => {
            if (!data.monthlyAverageFirstResponseTime) return 'No monthly data available';

            const months = Object.keys(data.monthlyAverageFirstResponseTime).sort();
            if (months.length < 2) return 'Insufficient data for trend';

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestData = data.monthlyAverageFirstResponseTime[latestMonth];
            const previousData = data.monthlyAverageFirstResponseTime[previousMonth];

            if (!latestData || !previousData) return 'Insufficient data for trend';

            const difference = latestData.average - previousData.average;
            const percentChange = ((difference / previousData.average) * 100).toFixed(1);
            const trend = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';

            const diffTime = formatResolutionTime(difference);

            // For response time, lower is better
            return `${trend} ${diffTime} (${Math.abs(Number(percentChange))}%) vs previous month`;
          })()}
          backDescriptionColor={(() => {
            if (!data.monthlyAverageFirstResponseTime) return metricCardValueColorNeutral;

            const months = Object.keys(data.monthlyAverageFirstResponseTime).sort();
            if (months.length < 2) return metricCardValueColorNeutral;

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestAvg = data.monthlyAverageFirstResponseTime[latestMonth]?.average;
            const previousAvg = data.monthlyAverageFirstResponseTime[previousMonth]?.average;

            if (!latestAvg || !previousAvg) return metricCardValueColorNeutral;

            // Lower response time is better
            return latestAvg <= previousAvg ? metricCardValueColorGood : metricCardValueColorBad;
          })()}
        />

        {/* Average Resolution Time */}
        <MetricCard
          title="Resolution Time (Snapshot)"
          frontCardColor={(() => {
            if (!data.avgResolutionTime) return metricCardColorNeutral;
            if (data.avgResolutionTime < 300) { //2 hours
              return metricCardColorGood;
            } else {
              return metricCardColorBad;
            }
          })()}
          value={(() => {
            if (!data.avgResolutionTime) return 'No data';
            return formatResolutionTime(data.avgResolutionTime);
          })()}

          frontValueColor={(() => {
            if (!data.avgResolutionTime) return metricCardValueColorNeutral;
            if (data.avgResolutionTime < 300) { //2 hours
              return metricCardValueColorGood;
            } else {
              return metricCardValueColorBad;
            }
          })()}
          description="Time to close cases"
          descriptionColor={(() => {
            if (!data.avgResolutionTime) return metricCardValueColorNeutral;
            if (data.avgResolutionTime < 300) { //2 hours
              return metricCardValueColorGood;
            } else {
              return metricCardValueColorBad;
            }
          })()}
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12,6 12,12 16,14" />
            </svg>
          }
          canFlip={true}
          backCardColor={(() => {
            if (!data.monthlyAverageResolutionTime) return metricCardColorNeutral;

            const months = Object.keys(data.monthlyAverageResolutionTime).sort();
            if (months.length < 2) return metricCardColorNeutral;

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestAvg = data.monthlyAverageResolutionTime[latestMonth]?.average;
            const previousAvg = data.monthlyAverageResolutionTime[previousMonth]?.average;

            if (!latestAvg || !previousAvg) return metricCardColorNeutral;

            // Lower resolution time is better
            return latestAvg <= previousAvg ? metricCardColorGood : metricCardColorBad;
          })()}
          backTitle='Resolution Time (Current Month)'
          backValue={(() => {
            if (!data.monthlyAverageResolutionTime) return 'No data';

            const months = Object.keys(data.monthlyAverageResolutionTime).sort();
            if (months.length === 0) return 'No data';

            const latestMonth = months[months.length - 1];
            const latestData = data.monthlyAverageResolutionTime[latestMonth];

            if (!latestData) return 'No data';

            return formatResolutionTime(latestData.average);
          })()}
          backValueColor={(() => {
            if (!data.monthlyAverageResolutionTime) return metricCardValueColorNeutral;

            const months = Object.keys(data.monthlyAverageResolutionTime).sort();
            if (months.length < 2) return metricCardValueColorNeutral;

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestAvg = data.monthlyAverageResolutionTime[latestMonth]?.average;
            const previousAvg = data.monthlyAverageResolutionTime[previousMonth]?.average;

            if (!latestAvg || !previousAvg) return metricCardValueColorNeutral;

            // Lower resolution time is better
            return latestAvg <= previousAvg ? metricCardValueColorGood : metricCardValueColorBad;
          })()}
          backDescription={(() => {
            if (!data.monthlyAverageResolutionTime) return 'No monthly data available';

            const months = Object.keys(data.monthlyAverageResolutionTime).sort();
            if (months.length < 2) return 'Insufficient data for trend';

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestData = data.monthlyAverageResolutionTime[latestMonth];
            const previousData = data.monthlyAverageResolutionTime[previousMonth];

            if (!latestData || !previousData) return 'Insufficient data for trend';

            const difference = latestData.average - previousData.average;
            const percentChange = ((difference / previousData.average) * 100).toFixed(1);
            const trend = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';
            const diffTime = formatResolutionTime(difference);

            // For resolution time, lower is better
            return `${trend} ${diffTime} (${Math.abs(Number(percentChange))}%) vs previous month`;
          })()}
          backDescriptionColor={(() => {
            if (!data.monthlyAverageResolutionTime) return metricCardValueColorNeutral;

            const months = Object.keys(data.monthlyAverageResolutionTime).sort();
            if (months.length < 2) return metricCardValueColorNeutral;

            const latestMonth = months[months.length - 1];
            const previousMonth = months[months.length - 2];

            const latestAvg = data.monthlyAverageResolutionTime[latestMonth]?.average;
            const previousAvg = data.monthlyAverageResolutionTime[previousMonth]?.average;

            if (!latestAvg || !previousAvg) return metricCardValueColorNeutral;

            // Lower resolution time is better
            return latestAvg <= previousAvg ? metricCardValueColorGood : metricCardValueColorBad;
          })()}
        />
      </div>

      {/* Key Metrics Slider - Mobile */}
      <div 
        style={{ 
          display: isMobile ? 'block' : 'none',
          marginBottom: '2rem',
          position: 'relative',
          width: '95vw',
          maxWidth: '90vw',
          paddingLeft: '1.5rem',
          paddingRight: '1rem',
          boxSizing: 'border-box',
          overflow: 'visible'
        }}
      >
        <MobileMetricSlider 
          cards={[
            // High Priority Open Cases
            <MetricCard
              title="Critical/High Priority Open"
              frontCardColor={(() => {
                if (highPriorityOpenCases.count < 1) { //should be no open cases
                  return metricCardColorGood;
                } else {
                  return metricCardColorBad;
                }
              })()}
              description={`${highPriorityOpenCases.percent.toFixed(1)}% of ${data.casesOpen} open cases`}
              descriptionColor={(() => {
                if (highPriorityOpenCases.count < 1) { //should be no open cases
                  return metricCardValueColorGood;
                } else {
                  return metricCardValueColorBad;
                }
              })()}
              value={highPriorityOpenCases.count}
              frontValueColor={(() => {
                if (highPriorityOpenCases.count < 1) { //should be no open cases
                  return metricCardValueColorGood;
                } else {
                  return metricCardValueColorBad;
                }
              })()}
              isNegative={highPriorityOpenCases.count > 0}
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ transition: 'stroke 0.2s ease' }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
                  <line x1="12" y1="11" x2="12" y2="16" />
                </svg>
              }
              canFlip={true}
              backCardColor={(() => {
                if (!data.priorityMonthlyTrendStats) return metricCardColorNeutral;

                const months = Object.keys(data.priorityMonthlyTrendStats).sort();
                if (months.length < 2) return metricCardColorNeutral;

                const currentMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const currentCriticalOpened = data.priorityMonthlyTrendStats[currentMonth]?.critical?.opened || 0;
                const currentHighOpened = data.priorityMonthlyTrendStats[currentMonth]?.high?.opened || 0;
                const currentTotal = currentCriticalOpened + currentHighOpened;

                const previousCriticalOpened = data.priorityMonthlyTrendStats[previousMonth]?.critical?.opened || 0;
                const previousHighOpened = data.priorityMonthlyTrendStats[previousMonth]?.high?.opened || 0;
                const previousTotal = previousCriticalOpened + previousHighOpened;

                // For opened critical/high cases, lower is better
                return currentTotal <= previousTotal ? metricCardColorGood : metricCardColorBad;
              })()}
              backTitle='Critical/High Priority Opened This Month'
              backValue={(() => {
                if (!data.priorityMonthlyTrendStats) return 'No data';

                const months = Object.keys(data.priorityMonthlyTrendStats).sort();
                if (months.length === 0) return 'No data';

                const currentMonth = months[months.length - 1];
                const currentMonthData = data.priorityMonthlyTrendStats[currentMonth];

                if (!currentMonthData) return 'No data';

                // Sum opened cases for critical and high priorities
                const criticalOpened = currentMonthData.critical?.opened || 0;
                const highOpened = currentMonthData.high?.opened || 0;
                const totalOpened = criticalOpened + highOpened;

                return totalOpened.toString();
              })()}
              backValueColor={(() => {
                if (!data.priorityMonthlyTrendStats) return metricCardValueColorNeutral;

                const months = Object.keys(data.priorityMonthlyTrendStats).sort();
                if (months.length < 2) return metricCardValueColorNeutral;

                const currentMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const currentCriticalOpened = data.priorityMonthlyTrendStats[currentMonth]?.critical?.opened || 0;
                const currentHighOpened = data.priorityMonthlyTrendStats[currentMonth]?.high?.opened || 0;
                const currentTotal = currentCriticalOpened + currentHighOpened;

                const previousCriticalOpened = data.priorityMonthlyTrendStats[previousMonth]?.critical?.opened || 0;
                const previousHighOpened = data.priorityMonthlyTrendStats[previousMonth]?.high?.opened || 0;
                const previousTotal = previousCriticalOpened + previousHighOpened;

                // For opened critical/high cases, lower is better
                return currentTotal <= previousTotal ? metricCardValueColorGood : metricCardValueColorBad;
              })()}
              backDescription={(() => {
                if (!data.priorityMonthlyTrendStats) return 'No trend data available';

                const months = Object.keys(data.priorityMonthlyTrendStats).sort();
                if (months.length < 2) return 'Insufficient data for trend';

                const currentMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const currentCriticalOpened = data.priorityMonthlyTrendStats[currentMonth]?.critical?.opened || 0;
                const currentHighOpened = data.priorityMonthlyTrendStats[currentMonth]?.high?.opened || 0;
                const currentTotal = currentCriticalOpened + currentHighOpened;

                const previousCriticalOpened = data.priorityMonthlyTrendStats[previousMonth]?.critical?.opened || 0;
                const previousHighOpened = data.priorityMonthlyTrendStats[previousMonth]?.high?.opened || 0;
                const previousTotal = previousCriticalOpened + previousHighOpened;

                if (previousTotal === 0 && currentTotal === 0) return 'No change from previous month';
                if (previousTotal === 0) return `↑ ${currentTotal} more than previous month`;

                const difference = currentTotal - previousTotal;
                const percentChange = ((difference / previousTotal) * 100).toFixed(1);
                const trend = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';

                return `${trend} ${Math.abs(difference)} (${Math.abs(Number(percentChange))}%) vs previous month`;
              })()}
              backDescriptionColor={(() => {
                if (!data.priorityMonthlyTrendStats) return metricCardValueColorNeutral;

                const months = Object.keys(data.priorityMonthlyTrendStats).sort();
                if (months.length < 2) return metricCardValueColorNeutral;

                const currentMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const currentCriticalOpened = data.priorityMonthlyTrendStats[currentMonth]?.critical?.opened || 0;
                const currentHighOpened = data.priorityMonthlyTrendStats[currentMonth]?.high?.opened || 0;
                const currentTotal = currentCriticalOpened + currentHighOpened;

                const previousCriticalOpened = data.priorityMonthlyTrendStats[previousMonth]?.critical?.opened || 0;
                const previousHighOpened = data.priorityMonthlyTrendStats[previousMonth]?.high?.opened || 0;
                const previousTotal = previousCriticalOpened + previousHighOpened;

                // For opened critical/high cases, lower is better
                return currentTotal <= previousTotal ? metricCardValueColorGood : metricCardValueColorBad;
              })()}
            />,

            // CSAT Average Score
            <MetricCard
              title="Customer Satisfaction (Snapshot)"
              value={formatCsatScore(data.csatData?.CsatAverageScore)}
              description={data.csatData?.CsatTotalResponses
                ? `${data.csatData.CsatTotalResponses} responses`
                : 'No responses yet'
              }
              frontValueColor={
                data.csatData
                  ? ((data.csatData?.CsatAverageScore || 0) < 0.6
                    ? ((data.csatData?.CsatAverageScore || 0) < 0.4 ? metricCardValueColorBad : metricCardValueColorNeutral)
                    : metricCardValueColorGood)
                  : metricCardValueColorNeutral
              }
              descriptionColor={
                data.csatData
                  ? ((data.csatData?.CsatAverageScore || 0) < 0.6
                    ? ((data.csatData?.CsatAverageScore || 0) < 0.4 ? metricCardValueColorBad : metricCardValueColorNeutral)
                    : metricCardValueColorGood)
                  : metricCardValueColorNeutral
              }
              frontCardColor={
                data.csatData
                  ? ((data.csatData?.CsatAverageScore || 0) < 0.6
                    ? ((data.csatData?.CsatAverageScore || 0) < 0.4 ? metricCardColorBad : metricCardColorNeutral)
                    : metricCardColorGood)
                  : metricCardColorNeutral
              }
              backValueColor={
                (() => {
                  if (!data.csatMonthlyAverage) return metricCardValueColorNeutral;

                  const months = Object.keys(data.csatMonthlyAverage).sort();
                  if (months.length < 2) return metricCardValueColorNeutral;

                  const latestMonth = months[months.length - 1];
                  const previousMonth = months[months.length - 2];

                  const latestScore = data.csatMonthlyAverage[latestMonth].averageNormalizedScore;
                  const previousScore = data.csatMonthlyAverage[previousMonth].averageNormalizedScore;

                  return latestScore >= previousScore ? metricCardValueColorGood : metricCardValueColorBad;
                })()
              }
              backCardColor={
                (() => {
                  if (!data.csatMonthlyAverage) return metricCardColorNeutral;

                  const months = Object.keys(data.csatMonthlyAverage).sort();
                  if (months.length < 2) return metricCardColorNeutral;

                  const latestMonth = months[months.length - 1];
                  const previousMonth = months[months.length - 2];

                  const latestScore = data.csatMonthlyAverage[latestMonth].averageNormalizedScore;
                  const previousScore = data.csatMonthlyAverage[previousMonth].averageNormalizedScore;

                  return latestScore >= previousScore ? metricCardColorGood : metricCardColorBad;
                })()
              }
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                  <circle cx="9" cy="9" r="0.5" fill="currentColor" />
                  <circle cx="15" cy="9" r="0.5" fill="currentColor" />
                </svg>
              }
              canFlip={true}
              backTitle='Customer Satisfaction (Current Month)'
              backValue={(() => {
                if (!data.csatMonthlyAverage) return 'No data';

                // Get months in chronological order
                const months = Object.keys(data.csatMonthlyAverage).sort();
                if (months.length === 0) return 'No data';

                // Get the most recent month's score
                const latestMonth = months[months.length - 1];
                const latestScore = data.csatMonthlyAverage[latestMonth].averageNormalizedScore;

                return formatCsatScore(latestScore);
              })()}
              backDescription={(() => {
                if (!data.csatMonthlyAverage) return 'No trend data available';

                // Get months in chronological order
                const months = Object.keys(data.csatMonthlyAverage).sort();
                if (months.length < 2) return 'Not enough data for trend';

                // Get the most recent and previous month's scores
                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestNumberOfResponses = data.csatMonthlyAverage[latestMonth].totalResponses;
                const latestScore = data.csatMonthlyAverage[latestMonth].averageNormalizedScore;
                const previousScore = data.csatMonthlyAverage[previousMonth].averageNormalizedScore;



                // Calculate percentage change
                const percentChange = ((latestScore - previousScore) / previousScore) * 100;

                const trend = percentChange > 0 ? '↑' : percentChange < 0 ? '↓' : '→';

                // Format the description
                return `${trend} ${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(1)}% vs previous month. (${latestNumberOfResponses} responses)`;
              })()}
              backDescriptionColor={(() => {
                if (!data.csatMonthlyAverage) return metricCardValueColorNeutral;

                const months = Object.keys(data.csatMonthlyAverage).sort();
                if (months.length < 2) return metricCardValueColorNeutral;

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestScore = data.csatMonthlyAverage[latestMonth].averageNormalizedScore;
                const previousScore = data.csatMonthlyAverage[previousMonth].averageNormalizedScore;

                // Return green for positive trend, red for negative
                return latestScore >= previousScore ? metricCardValueColorGood : metricCardValueColorBad;
              })()}
            />,

            // SLA Breaches
            <MetricCard
              title="SLA Breaches (Snapshot)"
              value={data.slaBreaches.count}
              description={`${data.slaBreaches.percent.toFixed(1)}% of ${data.casesTotal} total cases`}
              isNegative={true}
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ transition: 'stroke 0.2s ease' }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
                  <line x1="12" y1="11" x2="12" y2="16" />
                </svg>
              }
              canFlip={true}
              backTitle='SLA Breaches (Current Month)'
              frontValueColor={data.slaBreaches.count > 0 ? metricCardValueColorBad : metricCardValueColorGood}
              descriptionColor={data.slaBreaches.count > 0 ? metricCardValueColorBad : metricCardValueColorGood}
              backValue={
                data.monthlySlaBreaches ?
                  (() => {
                    // Get the last two months of data
                    const months = Object.keys(data.monthlySlaBreaches)
                      .sort()
                      .slice(-2);

                    if (months.length === 0) return '0';

                    const latestMonth = months[months.length - 1];
                    return `${data.monthlySlaBreaches[latestMonth].breachCount} (${data.monthlySlaBreaches[latestMonth].breachRate.toFixed(1)}%)`;
                  })() : '0'
              }
              backDescription={
                data.monthlySlaBreaches ?
                  (() => {
                    // Get the last two months of data
                    const months = Object.keys(data.monthlySlaBreaches)
                      .sort()
                      .slice(-2);

                    if (months.length < 2) return '→ No breaches recorded';

                    const currentMonth = months[1];
                    const previousMonth = months[0];
                    const currentRate = data.monthlySlaBreaches[currentMonth].breachRate;
                    const previousRate = data.monthlySlaBreaches[previousMonth].breachRate;
                    const difference = currentRate - previousRate;

                    const trend = difference > 0 ? 'up' : difference < 0 ? 'down' : 'stable';
                    const trendSymbol = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';

                    return `${trendSymbol} ${Math.abs(difference).toFixed(1)}% vs previous month (${previousMonth})`;
                  })() : '→ No breaches recorded'
              }
              backDescriptionColor={(() => {
                if (!data.monthlySlaBreaches) return undefined;

                const months = Object.keys(data.monthlySlaBreaches)
                  .sort()
                  .slice(-2);

                if (months.length < 2) return metricCardValueColorGood;

                const currentMonth = months[1];
                const previousMonth = months[0];
                const currentRate = data.monthlySlaBreaches[currentMonth].breachRate;
                const previousRate = data.monthlySlaBreaches[previousMonth].breachRate;
                const difference = currentRate - previousRate;

                return difference > 0 ? metricCardValueColorBad : difference < 0 ? metricCardValueColorGood : metricCardValueColorNeutral;
              })()}

              backValueColor={(() => {
                if (!data.monthlySlaBreaches) return metricCardValueColorGood;

                const months = Object.keys(data.monthlySlaBreaches)
                  .sort()
                  .slice(-2);

                if (months.length < 2) return metricCardValueColorGood;

                const currentMonth = months[1];
                const previousMonth = months[0];
                const currentRate = data.monthlySlaBreaches[currentMonth].breachRate;
                const previousRate = data.monthlySlaBreaches[previousMonth].breachRate;
                const difference = currentRate - previousRate;

                return difference > 0 ? metricCardValueColorBad : difference < 0 ? metricCardValueColorGood : metricCardValueColorNeutral;
              })()}
              frontCardColor={
                data.slaBreaches.count > 0 ? metricCardColorBad : metricCardColorGood
              }
              backCardColor={(() => {
                if (!data.monthlySlaBreaches) return metricCardColorGood;

                const months = Object.keys(data.monthlySlaBreaches)
                  .sort()
                  .slice(-2);

                if (months.length < 2) return metricCardColorGood;

                const currentMonth = months[1];
                const previousMonth = months[0];
                const currentRate = data.monthlySlaBreaches[currentMonth].breachRate;
                const previousRate = data.monthlySlaBreaches[previousMonth].breachRate;
                const difference = currentRate - previousRate;

                return difference > 0 ? metricCardColorBad : difference < 0 ? metricCardColorGood : metricCardColorNeutral;
              })()}
            />,

            // Open Cases
            <MetricCard
              title="Still Open Cases (Snapshot)"
              frontCardColor={((data.casesOpen / data.casesTotal) * 100) > 20 ? metricCardColorBad : metricCardColorGood}
              value={data.casesOpen}
              frontValueColor={((data.casesOpen / data.casesTotal) * 100) > 20 ? metricCardValueColorBad : metricCardValueColorGood}
              description={`${((data.casesOpen / data.casesTotal) * 100).toFixed(1)}% of ${data.casesTotal} total raised cases`}
              descriptionColor={((data.casesOpen / data.casesTotal) * 100) > 20 ? metricCardValueColorBad : metricCardValueColorGood}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14,2 14,8 20,8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10,9 9,9 8,9" />
                </svg>
              }
              canFlip={true}
              backCardColor={(() => {
                if (!data.monthlyCaseStats) return metricCardColorNeutral;

                const months = Object.keys(data.monthlyCaseStats.monthlyCasesByProduct).sort();
                if (months.length < 2) return metricCardColorNeutral;

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestData = data.monthlyCaseStats.monthlyCasesByProduct[latestMonth]?.DefaultAllProduct;
                const previousData = data.monthlyCaseStats.monthlyCasesByProduct[previousMonth]?.DefaultAllProduct;

                if (!latestData || !previousData) return metricCardColorNeutral;

                // Lower opened cases is better
                return latestData.opened <= previousData.opened ?
                  metricCardColorGood : metricCardColorBad;
              })()}
              backTitle='Newly Opened Cases (Current Month)'
              backValue={(() => {
                if (!data.monthlyCaseStats) return '0';

                const months = Object.keys(data.monthlyCaseStats.monthlyCasesByProduct).sort();
                if (months.length === 0) return '0';

                const latestMonth = months[months.length - 1];
                const productData = data.monthlyCaseStats.monthlyCasesByProduct[latestMonth]?.DefaultAllProduct;

                return productData ? productData.opened.toString() : '0';
              })()}
              backValueColor={(() => {
                if (!data.monthlyCaseStats) return metricCardValueColorNeutral;

                const months = Object.keys(data.monthlyCaseStats.monthlyCasesByProduct).sort();
                if (months.length < 2) return metricCardValueColorNeutral;

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestData = data.monthlyCaseStats.monthlyCasesByProduct[latestMonth]?.DefaultAllProduct;
                const previousData = data.monthlyCaseStats.monthlyCasesByProduct[previousMonth]?.DefaultAllProduct;

                if (!latestData || !previousData) return metricCardValueColorNeutral;

                // Lower opened cases is better
                return latestData.opened <= previousData.opened ?
                  metricCardValueColorGood : metricCardValueColorBad;
              })()}
              backDescription={(() => {
                if (!data.monthlyCaseStats) return 'No monthly data available';

                const months = Object.keys(data.monthlyCaseStats.monthlyCasesByProduct).sort();
                if (months.length < 2) return 'Insufficient data for trend';

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestData = data.monthlyCaseStats.monthlyCasesByProduct[latestMonth]?.DefaultAllProduct;
                const previousData = data.monthlyCaseStats.monthlyCasesByProduct[previousMonth]?.DefaultAllProduct;

                if (!latestData || !previousData) return 'Insufficient data for trend';

                const difference = latestData.opened - previousData.opened;
                const percentChange = ((difference / previousData.opened) * 100).toFixed(1);
                const trend = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';

                return `${trend} ${Math.abs(difference)} cases (${Math.abs(Number(percentChange))}%) vs previous month`;
              })()}
              backDescriptionColor={(() => {
                if (!data.monthlyCaseStats) return metricCardValueColorNeutral;

                const months = Object.keys(data.monthlyCaseStats.monthlyCasesByProduct).sort();
                if (months.length < 2) return metricCardValueColorNeutral;

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestData = data.monthlyCaseStats.monthlyCasesByProduct[latestMonth]?.DefaultAllProduct;
                const previousData = data.monthlyCaseStats.monthlyCasesByProduct[previousMonth]?.DefaultAllProduct;

                if (!latestData || !previousData) return metricCardValueColorNeutral;

                // Lower opened cases is better
                return latestData.opened <= previousData.opened ?
                  metricCardValueColorGood : metricCardValueColorBad;
              })()}
            />,

            //average first response time 
            <MetricCard
              title="First Response Time (Snapshot)"
              description="Average Time to First Response"
              descriptionColor={(() => {
                if (!data.avgFirstResponseTime) return metricCardValueColorNeutral;
                if (data.avgFirstResponseTime < 120) { //2 hours
                  return metricCardValueColorGood;
                } else {
                  return metricCardValueColorBad;
                }
              })()}
              value={(() => {
                if (!data.avgFirstResponseTime) return 'No data';
                return formatResolutionTime(data.avgFirstResponseTime);
              })()}
              frontValueColor={(() => {
                if (!data.avgFirstResponseTime) return metricCardValueColorNeutral;
                if (data.avgFirstResponseTime < 120) { //2 hours
                  return metricCardValueColorGood;
                } else {
                  return metricCardValueColorBad;
                }
              })()}
              frontCardColor={(() => {
                if (!data.avgFirstResponseTime) return metricCardColorNeutral;
                if (data.avgFirstResponseTime < 120) { //2 hours
                  return metricCardColorGood;
                } else {
                  return metricCardColorBad;
                }
              })()}
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
                <line x1="12" y1="20" x2="12" y2="22" />
                <line x1="20" y1="12" x2="22" y2="12" />
                <line x1="2" y1="12" x2="4" y2="12" />
                {/* Stopwatch pushers (nobbly buttons) */}
                <circle cx="16" cy="0" r="0.5" />
                <circle cx="8" cy="0" r="0.5" />

              </svg>
              }
              canFlip={true}
              backTitle='First Response Time (Current Month)'
              backCardColor={(() => {
                if (!data.monthlyAverageFirstResponseTime) return metricCardColorNeutral;

                const months = Object.keys(data.monthlyAverageFirstResponseTime).sort();
                if (months.length < 2) return metricCardColorNeutral;

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestAvg = data.monthlyAverageFirstResponseTime[latestMonth]?.average;
                const previousAvg = data.monthlyAverageFirstResponseTime[previousMonth]?.average;

                if (!latestAvg || !previousAvg) return metricCardColorNeutral;

                // Lower response time is better
                return latestAvg <= previousAvg ? metricCardColorGood : metricCardColorBad;
              })()}
              backValue={(() => {
                if (!data.monthlyAverageFirstResponseTime) return 'No data';

                const months = Object.keys(data.monthlyAverageFirstResponseTime).sort();
                if (months.length === 0) return 'No data';

                const latestMonth = months[months.length - 1];
                const latestData = data.monthlyAverageFirstResponseTime[latestMonth];

                if (!latestData) return 'No data';

                return formatResolutionTime(latestData.average);
              })()}
              backValueColor={(() => {
                if (!data.monthlyAverageFirstResponseTime) return metricCardValueColorNeutral;

                const months = Object.keys(data.monthlyAverageFirstResponseTime).sort();
                if (months.length < 2) return metricCardValueColorNeutral;

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestAvg = data.monthlyAverageFirstResponseTime[latestMonth]?.average;
                const previousAvg = data.monthlyAverageFirstResponseTime[previousMonth]?.average;

                if (!latestAvg || !previousAvg) return metricCardValueColorNeutral;

                // Lower response time is better
                return latestAvg <= previousAvg ? metricCardValueColorGood : metricCardValueColorBad;
              })()}
              backDescription={(() => {
                if (!data.monthlyAverageFirstResponseTime) return 'No monthly data available';

                const months = Object.keys(data.monthlyAverageFirstResponseTime).sort();
                if (months.length < 2) return 'Insufficient data for trend';

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestData = data.monthlyAverageFirstResponseTime[latestMonth];
                const previousData = data.monthlyAverageFirstResponseTime[previousMonth];

                if (!latestData || !previousData) return 'Insufficient data for trend';

                const difference = latestData.average - previousData.average;
                const percentChange = ((difference / previousData.average) * 100).toFixed(1);
                const trend = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';

                const diffTime = formatResolutionTime(difference);

                // For response time, lower is better
                return `${trend} ${diffTime} (${Math.abs(Number(percentChange))}%) vs previous month`;
              })()}
              backDescriptionColor={(() => {
                if (!data.monthlyAverageFirstResponseTime) return metricCardValueColorNeutral;

                const months = Object.keys(data.monthlyAverageFirstResponseTime).sort();
                if (months.length < 2) return metricCardValueColorNeutral;

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestAvg = data.monthlyAverageFirstResponseTime[latestMonth]?.average;
                const previousAvg = data.monthlyAverageFirstResponseTime[previousMonth]?.average;

                if (!latestAvg || !previousAvg) return metricCardValueColorNeutral;

                // Lower response time is better
                return latestAvg <= previousAvg ? metricCardValueColorGood : metricCardValueColorBad;
              })()}
            />,

            // Average Resolution Time
            <MetricCard
              title="Resolution Time (Snapshot)"
              frontCardColor={(() => {
                if (!data.avgResolutionTime) return metricCardColorNeutral;
                if (data.avgResolutionTime < 300) { //2 hours
                  return metricCardColorGood;
                } else {
                  return metricCardColorBad;
                }
              })()}
              value={(() => {
                if (!data.avgResolutionTime) return 'No data';
                return formatResolutionTime(data.avgResolutionTime);
              })()}

              frontValueColor={(() => {
                if (!data.avgResolutionTime) return metricCardValueColorNeutral;
                if (data.avgResolutionTime < 300) { //2 hours
                  return metricCardValueColorGood;
                } else {
                  return metricCardValueColorBad;
                }
              })()}
              description="Time to close cases"
              descriptionColor={(() => {
                if (!data.avgResolutionTime) return metricCardValueColorNeutral;
                if (data.avgResolutionTime < 300) { //2 hours
                  return metricCardValueColorGood;
                } else {
                  return metricCardValueColorBad;
                }
              })()}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12,6 12,12 16,14" />
                </svg>
              }
              canFlip={true}
              backCardColor={(() => {
                if (!data.monthlyAverageResolutionTime) return metricCardColorNeutral;

                const months = Object.keys(data.monthlyAverageResolutionTime).sort();
                if (months.length < 2) return metricCardColorNeutral;

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestAvg = data.monthlyAverageResolutionTime[latestMonth]?.average;
                const previousAvg = data.monthlyAverageResolutionTime[previousMonth]?.average;

                if (!latestAvg || !previousAvg) return metricCardColorNeutral;

                // Lower resolution time is better
                return latestAvg <= previousAvg ? metricCardColorGood : metricCardColorBad;
              })()}
              backTitle='Resolution Time (Current Month)'
              backValue={(() => {
                if (!data.monthlyAverageResolutionTime) return 'No data';

                const months = Object.keys(data.monthlyAverageResolutionTime).sort();
                if (months.length === 0) return 'No data';

                const latestMonth = months[months.length - 1];
                const latestData = data.monthlyAverageResolutionTime[latestMonth];

                if (!latestData) return 'No data';

                return formatResolutionTime(latestData.average);
              })()}
              backValueColor={(() => {
                if (!data.monthlyAverageResolutionTime) return metricCardValueColorNeutral;

                const months = Object.keys(data.monthlyAverageResolutionTime).sort();
                if (months.length < 2) return metricCardValueColorNeutral;

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestAvg = data.monthlyAverageResolutionTime[latestMonth]?.average;
                const previousAvg = data.monthlyAverageResolutionTime[previousMonth]?.average;

                if (!latestAvg || !previousAvg) return metricCardValueColorNeutral;

                // Lower resolution time is better
                return latestAvg <= previousAvg ? metricCardValueColorGood : metricCardValueColorBad;
              })()}
              backDescription={(() => {
                if (!data.monthlyAverageResolutionTime) return 'No monthly data available';

                const months = Object.keys(data.monthlyAverageResolutionTime).sort();
                if (months.length < 2) return 'Insufficient data for trend';

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestData = data.monthlyAverageResolutionTime[latestMonth];
                const previousData = data.monthlyAverageResolutionTime[previousMonth];

                if (!latestData || !previousData) return 'Insufficient data for trend';

                const difference = latestData.average - previousData.average;
                const percentChange = ((difference / previousData.average) * 100).toFixed(1);
                const trend = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';
                const diffTime = formatResolutionTime(difference);

                // For resolution time, lower is better
                return `${trend} ${diffTime} (${Math.abs(Number(percentChange))}%) vs previous month`;
              })()}
              backDescriptionColor={(() => {
                if (!data.monthlyAverageResolutionTime) return metricCardValueColorNeutral;

                const months = Object.keys(data.monthlyAverageResolutionTime).sort();
                if (months.length < 2) return metricCardValueColorNeutral;

                const latestMonth = months[months.length - 1];
                const previousMonth = months[months.length - 2];

                const latestAvg = data.monthlyAverageResolutionTime[latestMonth]?.average;
                const previousAvg = data.monthlyAverageResolutionTime[previousMonth]?.average;

                if (!latestAvg || !previousAvg) return metricCardValueColorNeutral;

                // Lower resolution time is better
                return latestAvg <= previousAvg ? metricCardValueColorGood : metricCardValueColorBad;
              })()}
            />


          ]}
        />
      </div>

      {/* At Risk and Referenceable Customers Section - Mobile Only */}
      {isMobile && data.customerRiskPercentages && Object.keys(data.customerRiskPercentages).length > 0 && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr', 
          gap: '1rem', 
          marginBottom: '2rem' 
        }}>
          {/* Top 3 At Risk Customers */}
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem', color: '#e53935' }}>
              🚨 Top At-Risk Customers
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {Object.entries(data.customerRiskPercentages)
                .sort(([, scoreA], [, scoreB]) => scoreB - scoreA)
                .slice(0, 3)
                .map(([customerName, riskScore], index) => {
                  const customerData = data.casesByAccount[customerName];
                  return (
                    <div
                      key={customerName}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        backgroundColor: index === 0 ? '#ffebee' : index === 1 ? '#fff3e0' : '#fafafa',
                        borderRadius: '8px',
                        border: `2px solid ${index === 0 ? '#ffcdd2' : index === 1 ? '#ffe0b2' : '#eeeeee'}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        overflowX: 'auto',
                        maxWidth: '100vw'
                      }}
                      onClick={() => navigateToCustomer(customerName)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: index === 0 ? '#f44336' : index === 1 ? '#ff9800' : '#9e9e9e',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        marginRight: '12px'
                      }}>
                        {index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
                          {customerName}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                        • {customerData?.openCases || 0} open cases <br />• {formatResolutionTime(customerData?.avgResolutionTime || 0)} avg resolution
                        </div>
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: index === 0 ? '#d32f2f' : index === 1 ? '#f57c00' : '#757575'
                      }}>
                        Risk Score:<br /> {riskScore.toFixed(1)}%
                        
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Top 3 Referenceable Customers */}
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem', color: '#43a047', overflowX: 'auto', maxWidth: '100vw' }}>
              ⭐ Top Referenceable Customers
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {Object.entries(data.customerRiskPercentages)
                .sort(([, scoreA], [, scoreB]) => scoreA - scoreB)
                .slice(0, 3)
                .map(([customerName, riskScore], index) => {
                  const customerData = data.casesByAccount[customerName];
                  return (
                    <div
                      key={customerName}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        backgroundColor: index === 0 ? '#e8f5e8' : index === 1 ? '#f3e5f5' : '#fafafa',
                        borderRadius: '8px',
                        border: `2px solid ${index === 0 ? '#c8e6c9' : index === 1 ? '#e1bee7' : '#eeeeee'}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onClick={() => navigateToCustomer(customerName)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: index === 0 ? '#4caf50' : index === 1 ? '#9c27b0' : '#9e9e9e',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        marginRight: '12px'
                      }}>
                        {index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
                          {customerName}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                        • {customerData?.openCases || 0} open cases <br />• {formatResolutionTime(customerData?.avgResolutionTime || 0)} avg resolution
                        </div>
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: index === 0 ? '#2e7d32' : index === 1 ? '#7b1fa2' : '#757575'
                      }}>
                        Risk Score: <br /> {riskScore.toFixed(1)}%
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Layout container - responsive */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
        gap: '1rem',
        marginBottom: isMobile ? '2rem' : '2rem'
      }}>
        {/* Left Column - AI Panel (Desktop) / Charts Column */}
        <div>
          {/* AI Insight Panel - Desktop only */}
          {!isMobile && (
            <div style={{
              height: '555px',
              marginBottom: shouldStack ? '1rem' : '0'
            }}>
              <StaticAiPanel
                insight={data.aiSummary?.summary || ""}
                createdOn={data.aiSummary?.createdOn ? data.aiSummary.createdOn : ""}
                entityName={"Support Overview"}
                contextType="support-overview"
                entityId={data.summaryId?.toString()}
                canGenerate={canGenerateAiSummary(data.aiSummary?.createdOn)}
              />
            </div>
          )}
        </div>

        {/* Right Column - Priority Status Breakdown and backlog aging*/}
        <div style={{ 
          display: 'grid', 
          gridTemplateRows: shouldStack ? '1fr 1fr' : 'auto auto', 
          gap: isMobile ? '2rem' : '1rem',
          marginTop: isMobile ? '0' : '0'
        }}>
          {/* Priority Status Breakdown Chart */}
          <div className="dashboard-card">
            <div style={{ 
              width: '100%', 
              height: shouldStack ? '320px' : '280px' 
            }}>
              <Bar
                data={processPriorityStatusData()!}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      stacked: true,
                      title: {
                        display: true,
                        text: 'Priority'
                      }
                    },
                    y: {
                      stacked: true,
                      title: {
                        display: true,
                        text: 'Number of Cases'
                      },
                      beginAtZero: true
                    }
                  },
                  plugins: {
                    tooltip: {
                      mode: 'index',
                      intersect: false
                    },
                    legend: {
                      position: 'top',
                      labels: {
                        boxWidth: 12,
                        usePointStyle: true
                      }
                    },
                    title: {
                      display: true,
                      text: 'Breakdown Of Open Cases',
                      font: {
                        size: 16,
                        weight: 'bold',
                      },
                    }
                  }
                }}
              />
            </div>
          </div>

          {/*backlog aging profile*/}
          <div className="dashboard-card" style={{ 
            position: 'relative',
            marginBottom: shouldStack ? '0rem' : '0'
          }}>
            {/* Info Icon */}
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              cursor: 'pointer',
              zIndex: 10,
              padding: '8px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
              onClick={(e) => {
                e.stopPropagation();
                console.log('Info icon clicked, current state:', showBacklogTooltip);
                setShowBacklogTooltip(!showBacklogTooltip);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#007bff';
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)';
                const svg = e.currentTarget.querySelector('svg') as SVGElement;
                if (svg) {
                  svg.style.stroke = 'white';
                  const circle = svg.querySelector('circle[fill]') as SVGCircleElement;
                  if (circle) circle.style.fill = 'white';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                const svg = e.currentTarget.querySelector('svg') as SVGElement;
                if (svg) {
                  svg.style.stroke = '#666';
                  const circle = svg.querySelector('circle[fill]') as SVGCircleElement;
                  if (circle) circle.style.fill = '#666';
                }
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#666"
                strokeWidth="2"
                style={{ transition: 'stroke 0.2s ease' }}
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="8" r="1" fill="#666" stroke="none" />
                <line x1="12" y1="11" x2="12" y2="16" />
              </svg>
            </div>
            {/* Tooltip */}
            {showBacklogTooltip && (
              <div
                style={{
                  position: 'absolute',
                  top: '55px',
                  right: '10px',
                  backgroundColor: '#333',
                  color: 'white',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  maxWidth: '280px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000,
                  border: '1px solid #555'
                }}
                onClick={(e) => e.stopPropagation()}
              >

                <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '15px' }}>
                  Backlog Aging Profile
                </div>
                <div>
                  Shows the distribution of open cases by how long they have been open. This helps identify aging patterns in your support backlog and prioritize older cases that may need immediate attention.
                </div>
                <div
                  style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '15px',
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#333',
                    transform: 'rotate(45deg)',
                    border: '1px solid #555',
                    borderBottom: 'none',
                    borderRight: 'none'
                  }}
                />
              </div>
            )}
            <div style={{ 
              width: '100%', 
              height: shouldStack ? '250px' : '200px' 
            }}>
              <Bar
                options={{
                  responsive: true,
                  maintainAspectRatio: false,

                  indexAxis: 'y', // Horizontal bar chart
                  scales: {
                    x: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Number of Open Cases'
                      }
                    },
                    y: {
                      title: {
                        display: true,
                        text: 'Time Open'
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      display: false
                    },
                    title: {
                      display: true,
                      text: 'Backlog Aging Profile',
                      font: {
                        size: 16,
                        weight: 'bold',
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          return `${context.parsed.x} cases`;
                        }
                      }
                    }
                  }
                }}
                data={processBacklogAgingData()!}
              />
            </div>

          </div>
        </div>
      </div>

      {/* Product Performance Charts (cases and resolution time) */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: shouldStack ? '1fr' : '1fr 1fr', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        {/* Cases Opened by Product Column */}
        <div className="dashboard-card" style={{ 
          display: 'grid', 
          gridTemplateColumns: shouldStack ? '1fr' : '3fr 1fr',
          gap: shouldStack ? '1rem' : '0',
          marginBottom: shouldStack ? '1rem' : '0'
        }}>
          <div style={{ 
            height: shouldStack ? '320px' : '400px',
            order: shouldStack ? 2 : 1
          }}>
            <Line
              options={casesOpenedOptions}
              data={getFilteredChartDataCases(productTrendData.casesOpenedData)}
            />
          </div>
          <div style={{ order: shouldStack ? 1 : 2 }}>
            <ProductLegend
              products={productTrendData.sortedProductsByAssignedCases}
              visibleProducts={visibleProductsCases}
              onToggleProduct={toggleProductCases}
              generateColor={generateColor}
              chartData={productTrendData.casesOpenedData}
              divisor={1}
              valuePostfix=""
              labelSumDecimalPlaces={0}
              isMobile={shouldStack}
            />
          </div>
        </div>

        {/* Average Resolution Time by Product Column */}
        <div className="dashboard-card" style={{ 
          display: 'grid', 
          gridTemplateColumns: shouldStack ? '1fr' : '3fr 1fr',
          gap: shouldStack ? '1rem' : '0'
        }}>
          <div style={{ 
            height: shouldStack ? '320px' : '400px',
            order: shouldStack ? 2 : 1
          }}>
            <Line
              options={avgResolutionTimeOptions}
              data={getFilteredChartDataResolution(productTrendData.avgResolutionTimeData)}
            />
          </div>
          <div style={{ order: shouldStack ? 1 : 2 }}>
            <ProductLegend
              products={productTrendData.sortedProductsByResolutionTime}
              visibleProducts={visibleProductsResolution}
              onToggleProduct={toggleProductResolution}
              generateColor={generateColor}
              chartData={productTrendData.avgResolutionTimeData}
              divisor={60}
              valuePostfix=" hrs"
              labelSumDecimalPlaces={1}
              isMobile={shouldStack}
            />
          </div>
        </div>
      </div>

      {/* At Risk and Referenceable Customers Section - Desktop Only */}
      {!isMobile && data.customerRiskPercentages && Object.keys(data.customerRiskPercentages).length > 0 && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: shouldStack ? '1fr' : '1fr 1fr', 
          gap: shouldStack ? '1rem' : '2rem', 
          marginBottom: '2rem' 
        }}>
          {/* Top 3 At Risk Customers */}
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem', color: '#e53935' }}>
              🚨 Top At-Risk Customers
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {Object.entries(data.customerRiskPercentages)
                .sort(([, scoreA], [, scoreB]) => scoreB - scoreA)
                .slice(0, 3)
                .map(([customerName, riskScore], index) => {
                  const customerData = data.casesByAccount[customerName];
                  return (
                    <div
                      key={customerName}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        backgroundColor: index === 0 ? '#ffebee' : index === 1 ? '#fff3e0' : '#fafafa',
                        borderRadius: '8px',
                        border: `2px solid ${index === 0 ? '#ffcdd2' : index === 1 ? '#ffe0b2' : '#eeeeee'}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        overflowX: 'auto',
                        maxWidth: '100vw'
                      }}
                      onClick={() => navigateToCustomer(customerName)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: index === 0 ? '#f44336' : index === 1 ? '#ff9800' : '#9e9e9e',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        marginRight: '12px'
                      }}>
                        {index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
                          {customerName}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {customerData?.openCases || 0} open cases • {formatResolutionTime(customerData?.avgResolutionTime || 0)} avg resolution
                        </div>
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: index === 0 ? '#d32f2f' : index === 1 ? '#f57c00' : '#757575'
                      }}>
                        Risk Score: {riskScore.toFixed(1)}%
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Top 3 Referenceable Customers */}
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem', color: '#43a047', overflowX: 'auto', maxWidth: '100vw' }}>
              ⭐ Top Referenceable Customers
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {Object.entries(data.customerRiskPercentages)
                .sort(([, scoreA], [, scoreB]) => scoreA - scoreB)
                .slice(0, 3)
                .map(([customerName, riskScore], index) => {
                  const customerData = data.casesByAccount[customerName];
                  return (
                    <div
                      key={customerName}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        backgroundColor: index === 0 ? '#e8f5e8' : index === 1 ? '#f3e5f5' : '#fafafa',
                        borderRadius: '8px',
                        border: `2px solid ${index === 0 ? '#c8e6c9' : index === 1 ? '#e1bee7' : '#eeeeee'}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onClick={() => navigateToCustomer(customerName)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: index === 0 ? '#4caf50' : index === 1 ? '#9c27b0' : '#9e9e9e',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        marginRight: '12px'
                      }}>
                        {index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
                          {customerName}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {customerData?.openCases || 0} open cases • {formatResolutionTime(customerData?.avgResolutionTime || 0)} avg resolution
                        </div>
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: index === 0 ? '#388e3c' : index === 1 ? '#7b1fa2' : '#757575'
                      }}>
                        Risk Score: {riskScore.toFixed(1)}%
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Monthly High Resolution Time Items Table */}
      {data.monthlyHighResolutionTimeItems && Object.keys(data.monthlyHighResolutionTimeItems).length > 0 && (() => {
        // Debug logging
        console.log('monthlyHighResolutionTimeItems:', data.monthlyHighResolutionTimeItems);
        console.log('Object.entries:', Object.entries(data.monthlyHighResolutionTimeItems));

        // Process the data
        const allItems = Object.entries(data.monthlyHighResolutionTimeItems)
          .flatMap(([month, monthData]) => {
            console.log(`Processing month ${month}:`, monthData);
            const items = Array.isArray(monthData) ? monthData : [];
            console.log(`Items for ${month}:`, items);
            return items.map((item, index) => ({ month, item, index }));
          })
          .sort((a, b) => b.item.resolutionTime - a.item.resolutionTime);

        console.log('All processed items:', allItems);

        // State for expansion

        const itemsToShow = isHighItemListExpanded ? allItems : allItems.slice(0, 5);
        const hasMoreItems = allItems.length > 5;

        return (
          <div className="dashboard-card" style={{ marginBottom: '2rem', overflowX: 'auto', maxWidth: '100vw' }}>
            <h3 style={{ 
              marginBottom: '1rem', 
              fontSize: shouldStack ? '16px' : '18px', 
              fontWeight: 'bold' 
            }}>
              High Resolution Time Cases ({isHighItemListExpanded ? allItems.length : `${itemsToShow.length} of ${allItems.length}`} items)
            </h3>
            <div style={{ overflowX: 'auto', maxWidth: '100vw' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: shouldStack ? '13px' : '14px',
                minWidth: shouldStack ? '900px' : 'auto' // Ensure horizontal scroll on mobile with better width
              }}>
                <thead>
                  <tr style={{
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #ddd'
                  }}>
                    <th style={{ 
                      padding: shouldStack ? '8px 6px' : '12px 8px', 
                      textAlign: 'center', 
                      fontWeight: 'bold', 
                      width: shouldStack ? '60px' : '50px' 
                    }}>Info</th>
                    <th style={{ 
                      padding: shouldStack ? '8px 6px' : '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold',
                      width: shouldStack ? '80px' : 'auto'
                    }}>Month</th>
                    <th style={{ 
                      padding: shouldStack ? '8px 6px' : '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold',
                      width: shouldStack ? '100px' : 'auto'
                    }}>Case ID</th>
                    <th style={{ 
                      padding: shouldStack ? '8px 6px' : '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold',
                      width: shouldStack ? '180px' : 'auto'
                    }}>Title</th>
                    <th style={{ 
                      padding: shouldStack ? '8px 6px' : '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold',
                      width: shouldStack ? '100px' : 'auto'
                    }}>Resolution Time</th>
                    <th style={{ 
                      padding: shouldStack ? '8px 6px' : '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold',
                      width: shouldStack ? '80px' : 'auto'
                    }}>vs Avg (×)</th>
                    <th style={{ 
                      padding: shouldStack ? '8px 6px' : '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold',
                      width: shouldStack ? '90px' : 'auto'
                    }}>Priority</th>
                    <th style={{ 
                      padding: shouldStack ? '8px 6px' : '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold',
                      width: shouldStack ? '100px' : 'auto'
                    }}>Assignee</th>
                    <th style={{ 
                      padding: shouldStack ? '8px 6px' : '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold',
                      width: shouldStack ? '90px' : 'auto'
                    }}>Product</th>
                    <th style={{ 
                      padding: shouldStack ? '8px 6px' : '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold',
                      width: shouldStack ? '100px' : 'auto'
                    }}>Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {itemsToShow.map(({ month, item, index }) => {

                    return (
                      <tr
                        key={`${month}-${item.id}-${index}`}
                        style={{
                          borderBottom: '1px solid #eee',
                          backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                        }}
                      >
                        <td style={{ 
                          padding: shouldStack ? '8px 6px' : '10px 8px', 
                          textAlign: 'center' 
                        }}>
                          <button
                            onClick={() => openModal(item)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#666',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#e3f2fd';
                              e.currentTarget.style.color = '#1976d2';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#666';
                            }}
                            title="View case details"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="3" />
                              <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" />
                            </svg>
                          </button>
                        </td>
                        <td style={{ 
                          padding: shouldStack ? '8px 6px' : '10px 8px'
                        }}>{month}</td>
                        <td style={{ 
                          padding: shouldStack ? '8px 6px' : '10px 8px', 
                          fontFamily: 'monospace'
                        }}>{item.number}</td>
                        <td style={{
                          padding: shouldStack ? '8px 6px' : '10px 8px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {shouldStack ? (item.title?.slice(0, 25) + '...' || 'N/A') : (item.title || 'N/A')}
                        </td>
                        <td style={{ 
                          padding: shouldStack ? '8px 6px' : '10px 8px'
                        }}>
                          {formatResolutionTime(item.resolutionTime)}
                        </td>
                        <td style={{
                          padding: shouldStack ? '8px 6px' : '10px 8px',
                          fontWeight: 'bold',
                          color: parseFloat(item.multipleOfAverage) > 2 ? '#e53935' : parseFloat(item.multipleOfAverage) > 1.5 ? '#ff9800' : '#43a047'
                        }}>
                          {item.multipleOfAverage}
                        </td>
                        <td style={{ 
                          padding: shouldStack ? '8px 6px' : '10px 8px' 
                        }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor:
                              (item.priority === 'high' || item.priority === 'critical') ? '#ffebee' :
                                item.priority === 'medium' ? '#fff3e0' :
                                  item.priority === 'low' ? '#e8f5e8' : '#f5f5f5',

                            color:
                              (item.priority === 'high' || item.priority === 'critical') ? '#c62828' :
                                item.priority === 'medium' ? '#ef6c00' :
                                  item.priority === 'low' ? '#2e7d32' : '#666'

                          }}>
                            {item.priority || 'N/A'}
                          </span>
                        </td>
                        <td style={{ 
                          padding: shouldStack ? '4px 2px' : '10px 8px' 
                        }}>
                          {item.assignee ? (
                            <button
                              onClick={() => navigateToTeamMember(item.assignee)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                padding: 0,
                                fontSize: 'inherit',
                                fontFamily: 'inherit'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = '#3949ab'}
                              onMouseLeave={(e) => e.currentTarget.style.color = '#1976d2'}
                            >
                              {shouldStack ? (item.assignee?.slice(0, 12) + '...' || 'N/A') : item.assignee}
                            </button>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td style={{ 
                          padding: shouldStack ? '8px 6px' : '10px 8px'
                        }}>{shouldStack ? (item.product?.slice(0, 10) + '...' || 'N/A') : (item.product || 'N/A')}</td>
                        <td style={{ 
                          padding: shouldStack ? '8px 6px' : '10px 8px' 
                        }}>
                          {item.account ? (
                            <button
                              onClick={() => navigateToCustomer(item.account)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                padding: 0,
                                fontSize: 'inherit',
                                fontFamily: 'inherit'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = '#3949ab'}
                              onMouseLeave={(e) => e.currentTarget.style.color = '#1976d2'}
                            >
                              {shouldStack ? (item.account?.slice(0, 12) + '...' || 'N/A') : item.account}
                            </button>
                          ) : (
                            'N/A'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasMoreItems && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <button
                  onClick={() => setIsHighItemListExpanded(!isHighItemListExpanded)}
                  style={{
                    background: 'none',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#666',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#bbb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#ddd';
                  }}
                >
                  {isHighItemListExpanded ? 'Show Less' : `Show All (${allItems.length})`}
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Modal for case details */}
      {isModalOpen && selectedItem && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: shouldStack ? '16px' : '24px',
              maxWidth: shouldStack ? '90vw' : '600px',
              maxHeight: '80vh',
              overflow: 'auto',
              margin: shouldStack ? '10px' : '20px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                Case Details: {selectedItem.number}
              </h3>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong>Title:</strong> {selectedItem.title || 'N/A'}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong>Description:</strong>
              <div style={{
                marginTop: '8px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                borderLeft: '4px solid #007bff',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.5'
              }}>
                {selectedItem.description || 'No description available'}
              </div>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: shouldStack ? '1fr' : '1fr 1fr', 
              gap: '16px', 
              fontSize: shouldStack ? '12px' : '14px' 
            }}>
              <div><strong>Resolution Time:</strong> {formatResolutionTime(selectedItem.resolutionTime)}</div>
              <div><strong>Priority:</strong> {selectedItem.priority || 'N/A'}</div>
              <div><strong>Assignee:</strong> {selectedItem.assignee || 'N/A'}</div>
              <div><strong>Product:</strong> {selectedItem.product || 'N/A'}</div>
              <div><strong>Customer:</strong> {selectedItem.account || 'N/A'}</div>
              <div><strong>vs Average ({formatResolutionTime(data.avgResolutionTime)}):</strong> {selectedItem.multipleOfAverage}×</div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile AI Drawer */}
      {isMobile && (
        <>
          {/* Drawer Handle */}
          <div 
            onClick={() => setDrawerOpen(!drawerOpen)}
            style={{
              position: 'fixed',
              bottom: drawerOpen ? '76vh' : '0',
              left: '0',
              right: '0',
              width: '100vw',
              height: '60px',
              background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(77, 182, 159) 162%)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              transition: 'bottom 0.3s ease-in-out',
              borderTopLeftRadius: drawerOpen ? '0' : '20px',
              borderTopRightRadius: drawerOpen ? '0' : '20px',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
              margin: 0,
              padding: 0
            }}
          >
            <div style={{
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '1rem',
              fontWeight: '500'
            }}>
              <span>Exec Insights</span>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                style={{
                  transform: drawerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <path d="M18 15l-6-6-6 6"/>
              </svg>
            </div>
          </div>

          {/* Drawer Content */}
          <div 
            style={{
              position: 'fixed',
              bottom: drawerOpen ? '0' : '-76vh',
              left: '0',
              right: '0',
              width: '100vw',
              height: '76vh',
              background: 'white',
              zIndex: 999,
              transition: 'bottom 0.3s ease-in-out',
              paddingBottom: '2rem',
              overflow: 'hidden',
              margin: 0,
              boxSizing: 'border-box'
            }}
          >
            <div style={{
              height: '100%',
              padding: '1rem',
              paddingTop: '2rem',
              paddingBottom: '2rem',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <StaticAiPanel
                insight={data.aiSummary?.summary || ""}
                createdOn={data.aiSummary?.createdOn ? data.aiSummary.createdOn : ""}
                entityName={"Support Overview"}
                contextType="support-overview"
                entityId={data.summaryId?.toString()}
                canGenerate={canGenerateAiSummary(data.aiSummary?.createdOn)}
              />
            </div>
          </div>

          {/* Overlay */}
          <div 
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              height: '100vh',
              width: '100vw',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: drawerOpen ? 998 : -1,
              opacity: drawerOpen ? 1 : 0,
              visibility: drawerOpen ? 'visible' : 'hidden',
              transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
              margin: 0,
              padding: 0,
              pointerEvents: drawerOpen ? 'auto' : 'none'
            }}
          />
        </>
      )}

    </div>
  );
};

export default SupportOverview;
